import React, { useEffect, useState } from "react";
import moment from "moment";
import "moment-duration-format";
import "twix";
import styled from "styled-components";
import ArchiveActions from "archive/ArchiveActions";
import { defaultCompare as naturalSort } from "logic/DefaultCompare";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import NumberUtils from "util/NumberUtils";
import { Button, Col, Input, Row, Table } from "components/bootstrap";
import { Icon, Timestamp, RelativeTime, Spinner, IfPermitted, PaginatedList } from "components/common";
import { IndicesActions } from "stores/indices/IndicesStore";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { StyledDescriptionWrapper } from "./StyledCatalogComponents";
const StyledTbody = styled.tbody(
  ({ $showDetails }) => `
  ${$showDetails ? "border-left: 4px solid #2980b9" : ""}
`
);
const StyledHr = styled.hr`
  margin-bottom: 5px;
  margin-top: 10px;
`;
const StyledP = styled.p`
  float: left;
`;
const Checkbox = styled(Input)`
  margin: 0;
`;
const DEFAULT_PAGE_SIZE = 5;
const DEFAULT_PAGE_SIZES = [5, 10, 20, 50];
const ArchiveCatalogTableEntry = ({
  archive,
  archiveContext,
  backendContext,
  onMarkArchive,
  checkedMarkedArchive
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [diskState, setDiskState] = useState({ loading: true, available: false });
  const [streamHistograms, setStreamHistograms] = useState([]);
  const [currentStreamHistogramPage, setCurrentStreamHistogramPage] = useState([]);
  const [streamHistogramPage, setStreamHistogramPage] = useState(1);
  const [streamHistogramPageSize, setStreamHistogramPageSize] = useState(DEFAULT_PAGE_SIZE);
  const sendTelemetry = useSendTelemetry();
  useEffect(() => {
    const { id_mappings, stream_histogram } = archive;
    const getStreamName = (streamId) => {
      if (!id_mappings || !id_mappings.streams) {
        return streamId;
      }
      return id_mappings.streams[streamId] || streamId;
    };
    const mapStreamHistograms = (sourceStreamHistograms) => Object.keys(sourceStreamHistograms).map((streamId) => ({
      streamId,
      name: getStreamName(streamId),
      count: sourceStreamHistograms[streamId]
    })).sort((a, b) => naturalSort(a.name, b.name));
    const getHistogramPageContent2 = (calcStreamHistograms2) => calcStreamHistograms2.slice(
      (streamHistogramPage - 1) * streamHistogramPageSize,
      streamHistogramPage * streamHistogramPageSize
    );
    const calculateStreamHistograms = (sourceStreamHistogram) => {
      const streams = {};
      Object.keys(sourceStreamHistogram).forEach((date) => {
        Object.keys(sourceStreamHistogram[date]).forEach((streamId) => {
          if (!streams[streamId]) {
            streams[streamId] = 0;
          }
          streams[streamId] += sourceStreamHistogram[date][streamId];
        });
      });
      return streams;
    };
    const calculatedStreamHistograms = calculateStreamHistograms(stream_histogram);
    const calcStreamHistograms = mapStreamHistograms(calculatedStreamHistograms);
    const histogramPageContent = getHistogramPageContent2(calcStreamHistograms);
    setStreamHistograms(calcStreamHistograms);
    setCurrentStreamHistogramPage(histogramPageContent);
  }, [archive, streamHistogramPage, streamHistogramPageSize]);
  const toggleDetails = (event) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.ITEM_DETAILS_TOGGLED, {
      app_pathname: "archive",
      app_section: "archive",
      event_details: {
        showing: !showDetails
      }
    });
    event.preventDefault();
    const expanding = !showDetails;
    if (expanding) {
      ArchiveActions.availability(archive.id).then((state) => {
        setDiskState({ ...state, loading: false });
      });
    }
    setShowDetails(expanding);
  };
  const _onRestoreIndex = () => () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.RESTORED, {
      app_pathname: "archive",
      app_section: "archive-catalog"
    });
    ArchiveActions.restoreArchive(archive.backend_id, archive.archive_id).then((result) => result);
  };
  const _deleteIndex = async (indexName) => {
    await IndicesActions.delete(indexName);
  };
  const _onDeleteArchive = () => () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.DELETED, {
      app_pathname: "archive",
      app_section: "archive-catalog"
    });
    if (
      // eslint-disable-next-line no-alert
      window.confirm(
        `Are you sure you want to delete archive "${archive.archive_id}" for index "${archive.index_name}"?
        This will also delete the restored indices for this archive if it exist.`
      )
    ) {
      ArchiveActions.deleteArchive(archive.backend_id, archive.archive_id).then(() => {
        if (archiveContext.restored) {
          _deleteIndex(archiveContext.restored_index_name);
        }
      });
    }
  };
  const _onDeleteIndex = (indexName) => () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.DELETED, {
      app_pathname: "archive",
      app_section: "archive-catalog"
    });
    if (window.confirm(`Are you sure you want to delete index ${indexName}?`)) {
      _deleteIndex(indexName);
    }
  };
  const getHistogramPageContent = (page, size) => streamHistograms.slice((page - 1) * size, page * size);
  const _onPageChange = (newPage, pageSize) => {
    if (streamHistograms.length > 0) {
      const newPageContent = getHistogramPageContent(newPage, pageSize);
      setCurrentStreamHistogramPage(newPageContent);
      setStreamHistogramPage(newPage);
      setStreamHistogramPageSize(pageSize);
    }
  };
  const renderStreamsDetails = () => {
    if (streamHistograms.length < 1) {
      return null;
    }
    const streamRows = currentStreamHistogramPage.map((histogramEntry) => /* @__PURE__ */ React.createElement("tr", { key: histogramEntry.streamId }, /* @__PURE__ */ React.createElement("td", null, histogramEntry.name), /* @__PURE__ */ React.createElement("td", null, NumberUtils.formatNumber(histogramEntry.count))));
    return /* @__PURE__ */ React.createElement(StyledDescriptionWrapper, { $marginLeft: 150 }, /* @__PURE__ */ React.createElement("div", { className: "table-responsive" }, /* @__PURE__ */ React.createElement(
      PaginatedList,
      {
        activePage: streamHistogramPage,
        onChange: _onPageChange,
        totalItems: streamHistograms.length,
        pageSize: DEFAULT_PAGE_SIZE,
        pageSizes: DEFAULT_PAGE_SIZES,
        useQueryParameter: false
      },
      /* @__PURE__ */ React.createElement(StyledP, null, "Number of archived messages per stream. ", /* @__PURE__ */ React.createElement("strong", null, "Note:"), " Messages can be in multiple streams."),
      /* @__PURE__ */ React.createElement(Table, { striped: true, bordered: true, condensed: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, "Stream"), /* @__PURE__ */ React.createElement("th", null, "Message count"))), /* @__PURE__ */ React.createElement("tbody", null, streamRows))
    )));
  };
  const renderDetails = () => {
    const segmentSize = archive.segments.reduce((sum, segment) => sum + segment.size, 0);
    const segmentRawSize = archive.segments.reduce((sum, segment) => sum + segment.raw_size, 0);
    const compressionType = archive.segments.map((segment) => segment.compression_type)[0];
    let restoreButton;
    if (archiveContext.restored) {
      restoreButton = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", bsSize: "xs", disabled: true }, "Restored as ", archiveContext.restored_index_name), "\xA0", /* @__PURE__ */ React.createElement(Button, { bsStyle: "danger", bsSize: "xs", onClick: _onDeleteIndex(archiveContext.restored_index_name) }, "Delete restored index"));
    } else {
      restoreButton = /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "archive:restore" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", bsSize: "xs", onClick: _onRestoreIndex() }, "Restore index"));
    }
    let rawSizeInfo;
    if (segmentRawSize > 0) {
      rawSizeInfo = ` / ${NumberUtils.formatBytes(segmentRawSize)} uncompressed`;
    }
    let archiveFileCheck;
    if (diskState.loading) {
      archiveFileCheck = /* @__PURE__ */ React.createElement(Spinner, { text: "Checking archive availability" });
    } else if (diskState.available) {
      archiveFileCheck = /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(Icon, { name: "check", className: "text-success" }), "\xA0Archive available");
    } else {
      archiveFileCheck = /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(Icon, { name: "close", className: "text-danger" }), "\xA0Archive not available");
    }
    const archiveDuration = moment.duration(archive.creation_duration, "milliseconds").format("h [h] m [min] s [sec] S [ms]");
    return /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 6 }, /* @__PURE__ */ React.createElement(StyledDescriptionWrapper, { $marginLeft: 150 }, /* @__PURE__ */ React.createElement("dl", null, /* @__PURE__ */ React.createElement("dt", null, "Index name:"), /* @__PURE__ */ React.createElement("dd", null, archive.index_name), /* @__PURE__ */ React.createElement("dt", null, "Backend:"), /* @__PURE__ */ React.createElement("dd", null, backendContext.title), /* @__PURE__ */ React.createElement("dt", null, "Created:"), /* @__PURE__ */ React.createElement("dd", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: archive.created_at }), " (took ", archiveDuration, ")"), /* @__PURE__ */ React.createElement("dt", null, "Message count:"), /* @__PURE__ */ React.createElement("dd", null, NumberUtils.formatNumber(archive.document_count)), /* @__PURE__ */ React.createElement("dt", null, "Earliest message:"), /* @__PURE__ */ React.createElement("dd", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: archive.timestamp_min })), /* @__PURE__ */ React.createElement("dt", null, "Latest message:"), /* @__PURE__ */ React.createElement("dd", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: archive.timestamp_max })), /* @__PURE__ */ React.createElement("dt", null, "Segment count:"), /* @__PURE__ */ React.createElement("dd", null, archive.segments.length), /* @__PURE__ */ React.createElement("dt", null, "Segments size:"), /* @__PURE__ */ React.createElement("dd", null, NumberUtils.formatBytes(segmentSize), " (compressed with ", compressionType, rawSizeInfo, ")"), /* @__PURE__ */ React.createElement("dt", null, "Segment directory:"), /* @__PURE__ */ React.createElement("dd", null, archiveContext.path), /* @__PURE__ */ React.createElement("dt", null, "Archive availability"), /* @__PURE__ */ React.createElement("dd", null, archiveFileCheck), archiveContext.restored && /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement("dt", null, "Restored index:"), /* @__PURE__ */ React.createElement("dd", null, archiveContext.restored_index_name))))), /* @__PURE__ */ React.createElement(Col, { md: 6 }, renderStreamsDetails())), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["archive:create", "archive:delete"] }, /* @__PURE__ */ React.createElement(StyledHr, null), restoreButton, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "archive:delete" }, "\xA0", /* @__PURE__ */ React.createElement(Button, { bsStyle: "danger", bsSize: "xs", onClick: _onDeleteArchive() }, "Delete archive"), " ")));
  };
  const renderStreamNames = () => {
    if (!archive.stream_names) {
      return /* @__PURE__ */ React.createElement("em", null, "No streams");
    }
    return archive.stream_names.sort((a, b) => naturalSort(a, b)).join(", ");
  };
  const contentRange = moment(archive.timestamp_min).twix(archive.timestamp_max);
  const preventEvent = (event) => {
    event.stopPropagation();
  };
  const currentUser = useCurrentUser();
  const colSpan = isPermitted(currentUser.permissions, "archive:delete") ? 8 : 7;
  return /* @__PURE__ */ React.createElement(StyledTbody, { className: "archive-catalog-entry", $showDetails: showDetails }, /* @__PURE__ */ React.createElement("tr", { onClick: toggleDetails, className: "toggle-details" }, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["archive:delete", "archive:restore"], anyPermissions: true }, /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement("div", { role: "presentation", onClick: preventEvent, onMouseDown: preventEvent }, /* @__PURE__ */ React.createElement(
    Checkbox,
    {
      type: "checkbox",
      id: archive.id,
      formGroupClassName: "mb-0",
      wrapperClassName: "mb-0",
      checked: checkedMarkedArchive(archive.id),
      onChange: onMarkArchive
    }
  )))), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement("strong", null, archive.index_name)), /* @__PURE__ */ React.createElement("td", null, backendContext.title), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(RelativeTime, { dateTime: archive.created_at })), /* @__PURE__ */ React.createElement("td", null, contentRange.format({ hourFormat: "H" })), /* @__PURE__ */ React.createElement("td", null, NumberUtils.formatNumber(archive.document_count), " msgs (", contentRange.humanizeLength(), ")"), /* @__PURE__ */ React.createElement("td", { className: "stretch" }, renderStreamNames()), /* @__PURE__ */ React.createElement("td", { className: "restored" }, archiveContext.restored ? /* @__PURE__ */ React.createElement(Icon, { name: "check" }) : null)), showDetails && /* @__PURE__ */ React.createElement("tr", { className: "archive-catalog-entries-details" }, /* @__PURE__ */ React.createElement("td", { colSpan }, renderDetails())));
};
export default ArchiveCatalogTableEntry;
